'use client';

import type { IOverall } from '@repo/sdk';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui';
import { formatDurationMs } from '@repo/utils';
import { BarChart3 } from 'lucide-react';
import type { JSX } from 'react';

function StatItem({
  label,
  value,
  color,
}: {
  label: string;
  value: string | number;
  color?: string;
}): JSX.Element {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-xl font-semibold ${color ?? ''}`}>{value}</div>
    </div>
  );
}

export function StatsOverview({ overall }: { overall: IOverall }): JSX.Element {
  const accuracy =
    overall.passed + overall.failed > 0
      ? Math.round((overall.passed / (overall.passed + overall.failed)) * 100)
      : 0;

  return (
    <Card className="md:col-span-2">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <BarChart3 className="h-5 w-5" />
          Overall Stats
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatItem label="Passed" value={overall.passed} color="text-green-600" />
          <StatItem label="Failed" value={overall.failed} color="text-red-500" />
          <StatItem label="Accuracy" value={`${accuracy}%`} color="text-blue-500" />
          <StatItem
            label="Time"
            value={formatDurationMs(overall.totalTimeSpent)}
            color="text-purple-500"
          />
          <StatItem label="Reviews" value={overall.totalReviews} />
          <StatItem label="Sessions" value={overall.totalSessions} />
          <StatItem label="Expired" value={overall.expired} color="text-orange-500" />
          <StatItem label="Skipped" value={overall.skipped} color="text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
}
